import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EnforcementCase } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATUSES = ['OPEN', 'NOTICE_ISSUED', 'COMPLIANT', 'NON_COMPLIANT', 'PROSECUTION_REFERRED', 'CLOSED'] as const;
const SOURCES = ['COMPLAINT', 'INSPECTION', 'PATROL'] as const;

export function EnforcementCases() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const canCreate = persona?.roles.includes('ENFORCEMENT') ?? false;

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (status) params.set('status', status);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/m17/enforcement-cases?${params.toString()}`;
  }, [status, q]);
  const list = useCursorList<EnforcementCase>(basePath);

  return (
    <div>
      <PageHeader
        title="Enforcement"
        subtitle="Unauthorised development: notice -> compliance check -> prosecution referral (M37 Judiciary)."
        actions={canCreate ? <button className="btn-primary" onClick={() => setShowNew(true)}>+ Open case</button> : undefined}
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status" className="w-48">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Search" className="min-w-[220px] flex-1">
          <input className="field-input" placeholder="Ref no or parcel no" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[600px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Ref no</th>
              <th className="table-head">Parcel</th>
              <th className="table-head">Source</th>
              <th className="table-head">Status</th>
              <th className="table-head">Opened</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((c) => (
              <tr key={c.id} className="table-row-link" onClick={() => navigate(`/planning/enforcement/${c.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{c.refNo}</td>
                <td className="table-cell">{c.parcelNo}</td>
                <td className="table-cell text-xs text-slate-500">{c.source}</td>
                <td className="table-cell"><StatusBadge status={c.status} /></td>
                <td className="table-cell text-xs text-slate-500">{formatDate(c.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading cases…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No enforcement cases found" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />

      {showNew ? (
        <NewCaseModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); navigate(`/planning/enforcement/${c.id}`); }} />
      ) : null}
    </div>
  );
}

function NewCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: EnforcementCase) => void }) {
  const [parcelNo, setParcelNo] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<(typeof SOURCES)[number]>('COMPLAINT');
  const [applicationId, setApplicationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<EnforcementCase>('/api/v1/m17/enforcement-cases', {
        parcelNo: parcelNo.trim(),
        description,
        source,
        applicationId: applicationId.trim() || undefined,
      });
      onCreated(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Open enforcement case" onClose={onClose} wide>
      <p className="mb-3 text-xs text-slate-500">No PlanningApplication necessarily exists — that is the point of unauthorised-development enforcement.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Parcel number">
          <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} placeholder="C9 GeoServe-validated" />
        </Field>
        <Field label="Source">
          <select className="field-input" value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Linked application id (optional)" className="sm:col-span-2">
          <input className="field-input" value={applicationId} onChange={(e) => setApplicationId(e.target.value)} />
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !parcelNo.trim() || description.length < 5} onClick={submit}>
          {busy ? 'Opening…' : 'Open case'}
        </button>
      </div>
    </Modal>
  );
}

export function EnforcementCaseDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const canAct = persona?.roles.includes('ENFORCEMENT') ?? false;
  const caseQ = useApiQuery<EnforcementCase>(`/api/v1/m17/enforcement-cases/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noticeText, setNoticeText] = useState('');
  const [complianceDays, setComplianceDays] = useState('30');
  const [complianceNotes, setComplianceNotes] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      caseQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (caseQ.loading) return <Spinner label="Loading case…" />;
  if (caseQ.error || !caseQ.data) return <ErrorNote message={caseQ.error ?? 'Case not found'} />;
  const c = caseQ.data;

  return (
    <div>
      <BackLink to="/planning/enforcement" />
      <PageHeader title={c.refNo} subtitle={`Parcel ${c.parcelNo} · ${c.source}`} actions={<StatusBadge status={c.status} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Description</h2>
          <p className="text-sm text-slate-600">{c.description}</p>

          {c.noticeText ? (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Compliance notice</h3>
              <p className="text-sm text-slate-600">{c.noticeText}</p>
              <p className="mt-1 text-xs text-slate-400">Issued {formatDateTime(c.noticeIssuedAt)} · deadline {formatDate(c.complianceDeadline)}</p>
            </div>
          ) : null}

          {c.complianceNotes ? (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Compliance check</h3>
              <p className="text-sm text-slate-600">{c.complianceNotes}</p>
              <p className="mt-1 text-xs text-slate-400">Checked {formatDateTime(c.complianceCheckedAt)}</p>
            </div>
          ) : null}

          {c.prosecutionReferralRef ? (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Prosecution referral</h3>
              <p className="text-sm text-slate-600">{c.prosecutionReferralRef} — {formatDateTime(c.prosecutionReferredAt)}</p>
            </div>
          ) : null}
        </div>

        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Actions</h2>
          {!canAct ? <p className="text-xs text-slate-400">Requires the ENFORCEMENT role.</p> : null}
          {canAct && c.status === 'OPEN' ? (
            <div className="mb-3 space-y-2">
              <Field label="Notice text">
                <textarea className="field-input" rows={3} value={noticeText} onChange={(e) => setNoticeText(e.target.value)} />
              </Field>
              <Field label="Compliance days">
                <input className="field-input" type="number" min="1" value={complianceDays} onChange={(e) => setComplianceDays(e.target.value)} />
              </Field>
              <button
                className="btn-primary w-full"
                disabled={busy || noticeText.length < 5}
                onClick={() => act(() => api.post(`/api/v1/m17/enforcement-cases/${id}/notice`, { noticeText, complianceDays: Number(complianceDays) }))}
              >
                Issue compliance notice
              </button>
            </div>
          ) : null}
          {canAct && c.status === 'NOTICE_ISSUED' ? (
            <div className="mb-3 space-y-2 border-t border-slate-100 pt-3">
              <Field label="Compliance notes">
                <textarea className="field-input" rows={2} value={complianceNotes} onChange={(e) => setComplianceNotes(e.target.value)} />
              </Field>
              <div className="flex gap-2">
                <button className="btn-success flex-1" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m17/enforcement-cases/${id}/compliance-check`, { compliant: true, notes: complianceNotes || undefined }))}>
                  Compliant
                </button>
                <button className="btn-danger flex-1" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m17/enforcement-cases/${id}/compliance-check`, { compliant: false, notes: complianceNotes || undefined }))}>
                  Non-compliant
                </button>
              </div>
            </div>
          ) : null}
          {canAct && c.status === 'NON_COMPLIANT' ? (
            <button className="btn-danger mb-3 w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m17/enforcement-cases/${id}/refer-prosecution`))}>
              Refer to prosecution (M37)
            </button>
          ) : null}
          {canAct && c.status !== 'CLOSED' ? (
            <button className="btn-secondary w-full" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m17/enforcement-cases/${id}/close`))}>
              Close case
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
